import React, { useState } from 'react';
import { ChipSelect, RankList, RankItem, ReadOnlyChips, ReadOnlyValue, RobbieOverlay, Textarea } from '../../ui';
import LocationAutocomplete from '../../common/LocationAutocomplete';
import ConnectedExtractedBomModal from '../../bom/extracted/ConnectedExtractedBomModal';
import AnalysisErrorBanner from './AnalysisErrorBanner';
import BuyerGstinField from './BuyerGstinField';
import CreditGate from './CreditGate';
import CriteriaStepBar from './CriteriaStepBar';
import ExtractedBomStrip from './ExtractedBomStrip';
import DiscoveryCredits from './DiscoveryCredits';
import useBomPricing from '../../../hooks/useBomPricing';
import useCriteriaStep from '../../../hooks/useCriteriaStep';
import useStep2CreditGate from '../../../hooks/useStep2CreditGate';
import { useReportStageActivity } from '../../../hooks/useStageActivity';
import { useLongWait } from '../../../hooks/useLongWait';
import { useReadOnly } from '../../../contexts/WizardAccessContext';
import { useRecordRobbieAction, useReportRobbieUiContext } from '../../../contexts/RobbieUiContext';
import { StageReport } from '../../../utils/stageActivity';
import { creditsCheckCopy, discoveryStartedToast } from '../../../utils/waitCopy';
import { useWizardToast } from '../../wizard/WizardToast';
import { EnhanceSelection, enhanceRequest } from '../../../utils/bomProposal';
import { ChatUiContext } from '../../../client/chatClient';
import { displayRfqId } from '../../../utils/rfqIdentity';
import { robbieActionText } from '../../../utils/robbieActivity';
import {
  COMPLIANCE_OPTIONS,
  LEAD_TIME_PRESETS,
  SOURCING_PRIORITY_OPTIONS,
} from '../../../utils/sourcingCriteria';

/**
 * Wizard Step 2 — Sourcing Criteria in the v2 layout: the Extracted BOM
 * strip, "What matters most" drag-to-rank card, Logistics & delivery
 * (delivery location + the optional sourcing location + lead-time chips),
 * Compliance standards with a live count,
 * Additional notes, and the StepBar. The balance check on entry runs under a
 * Robbie overlay; Start discovery shows its own busy button. Logic (store sync,
 * voice commands, save-then-analyze) lives in useCriteriaStep.
 *
 * A viewer reads the same criteria as saved values: the ranking still ranks
 * but does not drag, and the chips, location and notes render as what was
 * chosen rather than as inputs waiting for a choice.
 */
interface CriteriaStepProps {
  rfq: any;
  onNext: () => void;
  onBack: () => void;
  /** Sends Robbie a message on the buyer's behalf ("Enhance with Robbie"). */
  sendToRobbie?: (text: string, uiContext?: ChatUiContext) => void;
  /** Tells the wizard this step is mid-run, so Robbie drops the suggestion. */
  onWorking?: (stage: StageReport | null) => void;
}

// Display labels use the mock's en dash; stored values stay the backend's.
const LEAD_TIME_CHIPS = LEAD_TIME_PRESETS.map((value) => ({
  value,
  label: value.replace('-', '–'),
}));

const COMPLIANCE_CHIPS = COMPLIANCE_OPTIONS.map(({ value, label }) => ({ value, label }));

// Cities, states, countries — not street addresses. "Source suppliers from"
// is answered with a place you can search a market in, never a door number.
// Module-level so the autocomplete's fetch effect sees a stable reference.
const SOURCING_PLACE_TYPES = ['(regions)'];

const CriteriaStep: React.FC<CriteriaStepProps> = ({ rfq, onNext, onBack, sendToRobbie, onWorking }) => {
  const pricing = useBomPricing();
  const showToast = useWizardToast();
  // The toast lands with step 3a: what discovery cost and what is left.
  const step = useCriteriaStep(rfq, onNext, (started) => {
    const credits = started.discoveryCharge?.credits ?? null;
    const balance = pricing.balance !== null && credits !== null ? pricing.balance - credits : null;
    showToast(discoveryStartedToast(credits, started.totalComponents, balance));
  });
  const [bomOpen, setBomOpen] = useState(false);
  const readOnly = useReadOnly();
  const recordAction = useRecordRobbieAction();
  // Entering step 2 short of credits: pick the lines to take forward before
  // anything else. The form below is inert until the gate is answered.
  const creditGate = useStep2CreditGate({
    rfqId: rfq.rfqId,
    components: step.components,
    paidComponentIds: step.paidComponentIds,
    pricing,
    readOnly,
  });

  // Closed to the buyer while the gate is open, and while the rate and
  // balance it needs are still loading.
  const locked = creditGate.gate.needed || creditGate.gate.pending;

  // Save-and-start locks the whole form, so there is nothing to suggest.
  const checking = creditGate.gate.pending;
  const checkingLong = useLongWait(checking, creditsCheckCopy.longAfterMs);
  useReportStageActivity(
    onWorking,
    checking ? 'credits-check' : step.isAnalyzing ? 'criteria' : null,
    checking ? creditsCheckCopy.panel : undefined,
  );

  const rankItems: RankItem[] = step.priorities.map((p) => ({
    id: p.id,
    label: p.name,
    description: p.description,
  }));

  const handleReorder = (next: RankItem[]) => {
    const byId = new Map(SOURCING_PRIORITY_OPTIONS.map((o) => [o.id, o]));
    step.reorderPriorities(next.flatMap((item) => byId.get(item.id) ?? []));
  };

  const leadTimeLabel = LEAD_TIME_CHIPS.find((c) => c.value === step.leadTime)?.label;
  const complianceLabel = (value: string) =>
    COMPLIANCE_CHIPS.find((c) => c.value === value)?.label ?? value;

  // Robbie sees what the buyer sees. Unlike discovery/outreach this step's
  // selections are UNSAVED DRAFT state — they exist only on this screen until
  // save-and-analyze — so the values themselves ride along (the backend has
  // nothing to expand a pointer against).
  useReportRobbieUiContext({
    view: 'sourcing-criteria',
    detail:
      `unsaved selections on screen — lead time: ${leadTimeLabel || 'not picked yet'}; ` +
      `delivery location: ${step.location?.trim() || 'empty'}; ` +
      `source suppliers from: ${step.sourcingLocation?.trim() || 'anywhere (not set)'}; ` +
      `compliance: ${step.compliance.map(complianceLabel).join(', ') || 'none picked'}; ` +
      `priority order: ${step.priorities.map((p) => p.name).join(' > ')}; ` +
      `notes: ${step.notes?.trim() ? `"${step.notes.trim().slice(0, 80)}"` : 'empty'}`,
  });

  // Enhance closes the sheet and ASKS Robbie (no draft left to finish): his
  // answer, and any proposal card, arrive in the panel beside the step. The
  // view rides along explicitly — the sheet that would report it is closing.
  const handleEnhanceWithRobbie = readOnly || !sendToRobbie
    ? undefined
    : (selection?: EnhanceSelection) => {
        setBomOpen(false);
        const { text, uiContext } = enhanceRequest(step.components.length, selection);
        recordAction('chatAsk', robbieActionText.askedToReview(uiContext.componentIds?.length ?? null, step.components.length));
        sendToRobbie(text, uiContext);
      };

  return (
    <div className="relative flex flex-col gap-[16px] font-sans">
      <div data-testid="criteria-form" className="flex flex-col gap-[16px]" inert={locked} aria-hidden={locked || undefined}>
        <ExtractedBomStrip count={step.components.length} onOpen={() => setBomOpen(true)} />

        {step.analysisError && (
          <AnalysisErrorBanner message={step.analysisError} onDismiss={step.dismissAnalysisError} />
        )}

        <div id="sourcing-strategy-section" className="grid grid-cols-1 items-start gap-[16px] md:grid-cols-2">
          <div className="overflow-hidden rounded-card border border-line bg-surface">
            <div className="border-b border-line-divider-soft px-s6 py-[12px]">
              <div className="text-card-header text-ink">What matters most</div>
              <div className="mt-[2px] text-meta text-ink-muted">
                {readOnly
                  ? 'Robbie weights suppliers by this order'
                  : 'Drag to rank — Robbie weights suppliers by this order'}
              </div>
            </div>
            <RankList items={rankItems} onReorder={handleReorder} readOnly={readOnly} />
          </div>

          <div className="flex flex-col gap-[16px]">
            <div className="rounded-card border border-line bg-surface p-s6">
              <div className="mb-[12px] text-card-header text-ink">Logistics &amp; delivery</div>
              <label className="mb-s2 block text-meta font-semibold text-ink-secondary">
                Delivery location <span className="font-normal text-ink-muted">(optional)</span>
              </label>
              <div className="mb-s5">
                {readOnly ? (
                  <ReadOnlyValue value={step.location} emptyLabel="No delivery location set" />
                ) : (
                  <LocationAutocomplete
                    value={step.location}
                    onChange={step.changeLocation}
                    placeholder="e.g. Rotterdam, Netherlands"
                    disabled={step.isAnalyzing}
                    className="h-9 w-full rounded-control border border-line-strong bg-surface pl-9 text-body-md
                      text-ink placeholder:text-ink-muted transition-colors duration-100 hover:border-line-strong
                      focus:border-action focus:outline-none focus:ring-2 focus:ring-action-border"
                  />
                )}
              </div>
              <label className="mb-s2 block text-meta font-semibold text-ink-secondary">
                Source suppliers from <span className="font-normal text-ink-muted">(optional)</span>
              </label>
              <div className="mb-s5">
                {readOnly ? (
                  <ReadOnlyValue
                    value={step.sourcingLocation}
                    emptyLabel="Anywhere — no sourcing location set"
                  />
                ) : (
                  <LocationAutocomplete
                    value={step.sourcingLocation}
                    onChange={step.changeSourcingLocation}
                    placeholder="e.g. Coimbatore, India"
                    types={SOURCING_PLACE_TYPES}
                    disabled={step.isAnalyzing}
                    hint="Pick a suggestion or type a city, country or region, and I'll search there first. Leave it blank and I look worldwide."
                    className="h-9 w-full rounded-control border border-line-strong bg-surface pl-9 text-body-md
                      text-ink placeholder:text-ink-muted transition-colors duration-100 hover:border-line-strong
                      focus:border-action focus:outline-none focus:ring-2 focus:ring-action-border"
                  />
                )}
              </div>
              <label className="mb-s2 block text-meta font-semibold text-ink-secondary">
                Your GSTIN <span className="font-normal text-ink-muted">(optional)</span>
              </label>
              <div className="mb-s5">
                <BuyerGstinField rfqId={rfq.rfqId} initial={rfq.buyerGstin} readOnly={readOnly} disabled={step.isAnalyzing} />
              </div>
              <label className="mb-s2 block text-meta font-semibold text-ink-secondary">
                Desired lead time <span className="text-danger-ink">*</span>
              </label>
              {readOnly ? (
                <ReadOnlyValue value={leadTimeLabel} emptyLabel="No lead time set" />
              ) : (
                <ChipSelect
                  mode="single"
                  options={LEAD_TIME_CHIPS}
                  value={step.leadTime ? [step.leadTime] : []}
                  onChange={(next) => next[0] && step.changeLeadTime(next[0])}
                />
              )}
            </div>

            <div className="rounded-card border border-line bg-surface p-s6">
              <div className="mb-[12px] flex items-baseline justify-between">
                <span className="text-card-header text-ink">Compliance standards</span>
                <span className="text-meta text-ink-muted">{step.compliance.length} selected</span>
              </div>
              {readOnly ? (
                <ReadOnlyChips
                  values={step.compliance}
                  labelFor={complianceLabel}
                  emptyLabel="No compliance standards selected"
                />
              ) : (
                <ChipSelect
                  mode="multi"
                  options={COMPLIANCE_CHIPS}
                  value={step.compliance}
                  onChange={(next) => {
                    const changed =
                      next.find((v) => !step.compliance.includes(v)) ??
                      step.compliance.find((v) => !next.includes(v));
                    if (changed) step.toggleCompliance(changed);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="rounded-card border border-line bg-surface p-s6">
          <div className="mb-s4 text-card-header text-ink">Additional notes</div>
          {readOnly ? (
            <ReadOnlyValue value={step.notes} emptyLabel="No notes added" />
          ) : (
            <Textarea
              value={step.notes}
              onChange={(e) => step.changeNotes(e.target.value)}
              placeholder={`Anything I should know before I search — e.g. "Exclude suppliers from a specific country", "Need samples approved before bulk order", "Prefer net-30 payment terms", "Split delivery in two batches", "Supplier must have export experience"`}
              className="h-[96px] resize-none"
              aria-label="Additional notes"
            />
          )}
        </div>

        {/* Next to Continue: a short balance answers where it was pressed. */}
        <DiscoveryCredits exit={step.exit} components={step.components} rfqLabel={displayRfqId(rfq.rfqId)} />

        <ConnectedExtractedBomModal
          open={bomOpen}
          onClose={() => setBomOpen(false)}
          fileName={step.fileName}
          readOnly={readOnly}
          onEnhanceWithRobbie={handleEnhanceWithRobbie}
        />
      </div>
      <CriteriaStepBar
        location={step.location}
        leadTimeLabel={leadTimeLabel}
        components={step.components.length}
        rate={pricing.rate}
        readOnly={readOnly}
        starting={step.isAnalyzing}
        onBack={onBack}
        onStart={readOnly ? onNext : step.submit}
      />
      <RobbieOverlay open={checking} title={creditsCheckCopy.title} usual={creditsCheckCopy.usual} long={checkingLong} />
      <CreditGate state={creditGate} components={step.components} rfqLabel={displayRfqId(rfq.rfqId)} onBack={onBack} />
    </div>
  );
};

export default CriteriaStep;
